import * as React from "react";
import {
    ActionButton,
    AlertDialog,
    AlertDialogContent,
    Button,
    Dialog,
    DialogContent,
    Pagination,
} from "@jhool-io/fe-components";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Document, Page } from "react-pdf";
import { useFetchSingleRemitDocuments } from "../../hooks/remits.queries";
import { useDeleteRemitDocument } from "../../hooks/remits.mutations";
import { useFetchDocument } from "../../../../hooks/queries";
import useToast from "../../../../hooks/useToast";
import FilePicker from "../../../../components/FilePicker/FilePicker";
import ListState from "../../../../components/ListState/ListState";
import Loader from "../../../../shared-ui/Loader/Loader";
import { ClientDocumentTypes } from "../../../../utils/types/client";
import { SupportedUploadMethod } from "../../../../utils/types/filepicker";
import ArrowUpRightIcon from "../../../../icons/ArrowUpRight";
import DeleteIcon from "../../../../icons/Delete";
import ChevronRightIcon from "../../../../icons/ChevronRight";
import { PDF_DOCUMENT_OPTIONS } from "../../../../lib/pdfWorker";
import { addPracticeParamToUrl } from "../../../../utils/helpers";
import { useAppSelector } from "../../../../hooks/useRedux";

type RemitDocumentDialog = "add-doc" | "view-doc" | "delete-doc";

interface RemitDocumentsProps {
    remittanceId: string;
    remitName: string;
}

export default function RemitDocuments({
    remittanceId,
    remitName,
}: RemitDocumentsProps) {
    const [documentDialogInView, setDocumentDialogInView] =
        React.useState<RemitDocumentDialog | null>(null);
    const [documentInViewId, setDocumentInViewId] = React.useState<
        string | null
    >(null);
    const [documentToDeleteId, setDocumentToDeleteId] = React.useState<
        string | null
    >(null);
    const [documentPageNumber, setDocumentPageNumber] = React.useState(1);
    const [documentNumPages, setDocumentNumPages] = React.useState<
        null | number
    >(null);

    const { toast } = useToast();
    const queryClient = useQueryClient();

    const { practice } = useAppSelector((state) => state.userPractice);

    const singleRemitFiles = useFetchSingleRemitDocuments(
        remittanceId,
        Boolean(remittanceId)
    );

    const deleteDocument = useDeleteRemitDocument(
        remittanceId,
        documentToDeleteId || ""
    );

    const selectedDocumentDetails = singleRemitFiles.data?.data?.find(
        (item) => item.remittance_attachment_id === documentInViewId
    );

    const remitFile = useFetchDocument(
        selectedDocumentDetails?.url as string,
        documentDialogInView === "view-doc" &&
            Boolean(documentInViewId) &&
            Boolean(selectedDocumentDetails)
    );

    const contentType = selectedDocumentDetails?.metadata?.content_type || "";
    const isImage = /^image/.test(contentType);

    const handleCloseDocumentDialog = () => {
        setDocumentDialogInView(null);
        setDocumentInViewId(null);
        setDocumentPageNumber(1);
        setDocumentNumPages(null);
    };

    const handleDeleteDocument = () => {
        deleteDocument.mutate(null, {
            onSuccess: (res) => {
                queryClient.invalidateQueries({
                    queryKey: ["remit-docs", remittanceId],
                });
                toast({
                    mode: "success",
                    message:
                        res.message ||
                        "Insurance attachment deleted successfully!",
                });
                setDocumentToDeleteId(null);
                setDocumentDialogInView(null);
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not delete remit document at this time",
                });
            },
        });
    };

    const fileToDisplay = () => {
        if (selectedDocumentDetails && isImage) {
            return (
                <>
                    {remitFile.isLoading && (
                        <span className="text-sm flex items-center justify-center font-medium h-3">
                            Loading document....
                        </span>
                    )}
                    {remitFile.error && (
                        <span className="text-danger text-sm flex items-center justify-center font-medium h-3">
                            Could not load document at this time
                        </span>
                    )}
                    {remitFile.data && (
                        <div className="h-full w-full">
                            <img
                                className="w-full h-full object-contain"
                                src={`data:${contentType};base64,${remitFile.data?.data}`}
                                alt={selectedDocumentDetails?.name}
                            />
                        </div>
                    )}
                </>
            );
        }

        return (
            <>
                {remitFile.isLoading && (
                    <span className="text-base flex items-center justify-center font-medium h-3">
                        Loading pdf..
                    </span>
                )}
                {remitFile.error && (
                    <span className="text-danger text-base flex items-center justify-center font-medium h-3">
                        Could not load pdf at this time
                    </span>
                )}
                {remitFile.data && (
                    <Document
                        error={
                            <span className="text-danger text-base flex items-center justify-center font-medium h-3">
                                Could not load pdf at this time
                            </span>
                        }
                        loading={
                            <span className="text-base flex items-center justify-center font-medium h-3">
                                Loading pdf..
                            </span>
                        }
                        onLoadSuccess={({ numPages: nextNumPages }) => {
                            setDocumentNumPages(nextNumPages);
                            setDocumentPageNumber(1);
                        }}
                        onItemClick={({ pageNumber: itemPageNumber }) => {
                            setDocumentPageNumber(Number(itemPageNumber));
                        }}
                        file={`data:application/pdf;base64,${remitFile.data.data}`}
                        options={PDF_DOCUMENT_OPTIONS}
                    >
                        <Page
                            height={550}
                            scale={2}
                            pageNumber={documentPageNumber}
                            renderAnnotationLayer={false}
                        />
                        {documentNumPages && documentNumPages > 1 && (
                            <Pagination
                                totalCount={documentNumPages}
                                totalPages={documentNumPages}
                                currentPage={documentPageNumber}
                                onPageChange={(page) =>
                                    setDocumentPageNumber(page)
                                }
                                pageSize={1}
                                isCentered
                            />
                        )}
                    </Document>
                )}
            </>
        );
    };

    return (
        <>
            <Dialog
                open={
                    documentDialogInView === "add-doc" ||
                    documentDialogInView === "view-doc"
                }
            >
                <DialogContent
                    handleCloseDialog={handleCloseDocumentDialog}
                    variant={
                        documentDialogInView === "add-doc" ? "center" : "drawer"
                    }
                    showFooter={false}
                    title={
                        documentDialogInView === "add-doc"
                            ? "Upload document"
                            : ""
                    }
                >
                    {documentDialogInView === "add-doc" ? (
                        <FilePicker
                            name="filepicker"
                            uploadMethods={[
                                { method: SupportedUploadMethod.manualupload },
                                { method: SupportedUploadMethod.googledrive },
                                { method: SupportedUploadMethod.dropbox },
                            ]}
                            uploadType="remit-file"
                            successCallback={() => {
                                setDocumentDialogInView(null);
                            }}
                            documentToUploadType={ClientDocumentTypes.OTHER}
                            remitId={remittanceId}
                            remitName={remitName}
                        />
                    ) : (
                        fileToDisplay()
                    )}
                </DialogContent>
            </Dialog>

            <AlertDialog open={documentDialogInView === "delete-doc"}>
                <AlertDialogContent
                    title="Are you sure you want to delete this document?"
                    description="By deleting this document, you won’t be able to recover it."
                    saveText="Delete document"
                    cancelText="Cancel"
                    onCancelClick={() => setDocumentDialogInView(null)}
                    onSaveClick={handleDeleteDocument}
                    isDeleting
                    isSaveBtnDisabled={deleteDocument.isLoading}
                    isCancelBtnDisabled={deleteDocument.isLoading}
                />
            </AlertDialog>

            <div className="w-full rounded-md [border:1px_solid_var(--color-stroke)]">
                <div className="flex flex-col gap-y-3 items-start min-[500px]:flex-row min-[500px]:justify-between min-[500px]:items-center px-24 py-[29px]">
                    <h3 className="text-base font-bold">Documents</h3>
                    <Button
                        onClick={() => setDocumentDialogInView("add-doc")}
                        disabled={
                            singleRemitFiles.isLoading ||
                            Boolean(singleRemitFiles.error)
                        }
                    >
                        Upload document
                    </Button>
                </div>

                {singleRemitFiles.isLoading && <Loader />}

                {singleRemitFiles.error && (
                    <ListState
                        stateHelperText="Try reloading this page to solve this issue"
                        emptyMessage={
                            singleRemitFiles.error.response?.data.message ||
                            "Cannot display document at this time"
                        }
                        isError
                        context="general"
                    />
                )}

                {singleRemitFiles.data && (
                    <>
                        {(!singleRemitFiles.data.data ||
                            singleRemitFiles.data.data.length === 0) && (
                            <ListState
                                stateHelperText="Remit documents will appear here when added"
                                emptyMessage="No documents yet"
                                isError={false}
                                context="general"
                            />
                        )}
                        {singleRemitFiles.data.data &&
                            singleRemitFiles.data.data.length > 0 && (
                                <div className="p-24 grid gap-2 grid-cols-[repeat(auto-fill,minmax(262px,1fr))]">
                                    {singleRemitFiles.data.data.map(
                                        (attachment) => (
                                            <div
                                                className="flex flex-col gap-y-6 bg-[--card-bg-gray] rounded-md p-6 [border:1px_solid_var(--color-stroke)]"
                                                key={
                                                    attachment.remittance_attachment_id
                                                }
                                            >
                                                <span className="text-base flex items-center gap-x-[2px] text-text font-medium text-left">
                                                    {attachment.name}
                                                    <Link
                                                        to={addPracticeParamToUrl(
                                                            `/remit/${remittanceId}/${attachment.remittance_attachment_id}`,
                                                            practice?.practice_id
                                                        )}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="leading-[0px] link"
                                                    >
                                                        <ArrowUpRightIcon />
                                                    </Link>
                                                </span>

                                                <div className="flex justify-between items-center">
                                                    <ActionButton
                                                        variant="normal"
                                                        size="auto"
                                                        className="p-0"
                                                        onClick={() => {
                                                            setDocumentToDeleteId(
                                                                attachment.remittance_attachment_id
                                                            );
                                                            setDocumentDialogInView(
                                                                "delete-doc"
                                                            );
                                                        }}
                                                        actionTitle="Delete Document"
                                                    >
                                                        <DeleteIcon />
                                                    </ActionButton>
                                                    <Button
                                                        variant="can-hover"
                                                        size="auto"
                                                        onClick={() => {
                                                            setDocumentDialogInView(
                                                                "view-doc"
                                                            );
                                                            setDocumentInViewId(
                                                                attachment.remittance_attachment_id
                                                            );
                                                        }}
                                                        className="text-caption font-semibold text-textLink"
                                                    >
                                                        View doc
                                                        <ChevronRightIcon />
                                                    </Button>
                                                </div>
                                            </div>
                                        )
                                    )}
                                </div>
                            )}
                    </>
                )}
            </div>
        </>
    );
}
